import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from "@mui/material";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { EMAIL_REGEX } from "../../utils/constants";
import apiUsers from "../../services/authServiceLogin";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

// const URL_REGEX = /^https?:\/\/([a-z0-9-]+\.)+[a-z]{2,}([\/\w.-]*)*\/?$/i;

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function GlobalSetting() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [versionData, setVersionData] = useState([]);
  const [versionLoading, setVersionLoading] = useState(true);
  const [editingVersion, setEditingVersion] = useState(null);

  const settingData = async () => {
    try {
      setLoading(true);
      const response = await apiUsers.getSetting();
      setData(response.data.data);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    settingData();
    fetchVersionData();
  }, []);

  const fetchVersionData = async () => {
    try {
      setVersionLoading(true);
      const response = await apiUsers.getAllAppVersion();
      if (response.status === 200 && response.data.data) {
        setVersionData(response.data.data);
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setVersionLoading(false);
    }
  };


  const initialValues = {
    instaLink: data?.instaLink ?? "",
    fbLink: data?.fbLink ?? "",
    telegramLink: data?.telegramLink ?? "",
    contactUsEmail: data?.contactUsEmail ?? "",
    // commission: data?.commission ?? "",
    deliveryFee: data?.deliveryFee ?? 0,
    rewardPoints: data?.rewardPoints ?? "",
    rewardExpiry: data?.rewardExpiry ?? "",
    rewardThresholdValue: data?.rewardThresholdValue || "",
    contactUs: data?.contactUs && data?.contactUs.length > 0 ? data.contactUs : [],
    // dealerOrderRange: data?.dealerOrderRange ?? "",
    companyAddress: data?.companyAddress ?? "",
  };

  const validationSchema = Yup.object().shape({
    instaLink: Yup.string()
    .trim()
    .url("Instagram link must be a valid URL")
    .max(100, "Instagram link must be at most 100 characters")
    .required("Instagram Link is required"),

  fbLink: Yup.string()
    .trim()
    .url("Facebook link must be a valid URL")
    .max(100, "Facebook link must be at most 100 characters")
    .required("Facebook Link is required"),

  telegramLink: Yup.string()
    .trim()
    .url("Telegram link must be a valid URL")
    .max(100, "Telegram link must be at most 100 characters"),
    contactUsEmail: Yup.string()
      .required("Contact Us Email is required")
      .trim()
      .matches(EMAIL_REGEX, "Please enter a valid email address")
      .max(255),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      values.instaLink = values?.instaLink?.trim();
      values.fbLink = values?.fbLink?.trim();
      values.contactUsEmail = values?.contactUsEmail?.trim();
      values.rewardPoints = parseFloat(values.rewardPoints || 0);

      values.contactUs = values.contactUs.map((num) =>
        num.startsWith("+") ? num : `+${num}`
      );

      apiUsers
        .updateSetting(values)
        .then((res) => {
          if (res.status === 200) {
            toast.success(res?.data?.message);
          }
          setSubmitting(false);
          settingData();
        })
        .catch((err) => {
          errorHandler(err);
          setSubmitting(false);
        });
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  // Version Upgrade Form
  const versionInitialValues = {
    platform: "",
    appType: "",
    version: "",
    applink: "",
  };

  const versionValidationSchema = Yup.object().shape({
    version: Yup.string().required("Version is required"),
    applink: Yup.string()
      .url("App Link must be a valid URL")
      .required("App Link is required"),
  });

  const handleEditVersion = (version) => {
    setEditingVersion(version._id);
    versionFormik.setFieldValue("platform", version.platform || "");
    versionFormik.setFieldValue("appType", version.appType || "");
    versionFormik.setFieldValue("version", version.version || "");
    versionFormik.setFieldValue("applink", version.applink || "");
    versionFormik.setFieldValue("_id", version._id || "");
  };

  const handleCancelEdit = () => {
    setEditingVersion(null);
    versionFormik.resetForm();
  };

  const versionFormik = useFormik({
    enableReinitialize: true,
    initialValues: versionInitialValues,
    validationSchema: versionValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      const payload = {
        platform: values.platform,
        appType: values.appType,
        version: values.version,
        applink: values.applink.trim(),
      };
      
      if (values._id) {
        payload._id = values._id;
      }

      apiUsers
        .createOrUpdateAppVersion(payload)
        .then((res) => {
          if (res.status === 200) {
            toast.success(res?.data?.message || "Version updated successfully");
            fetchVersionData();
            setEditingVersion(null);
            versionFormik.resetForm();
          }
          setSubmitting(false);
        })
        .catch((err) => {
          errorHandler(err);
          setSubmitting(false);
        });
    },
  });

  const {
    errors: versionErrors,
    touched: versionTouched,
    isSubmitting: versionIsSubmitting,
    handleSubmit: versionHandleSubmit,
    getFieldProps: versionGetFieldProps,
  } = versionFormik;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" sx={{ mb: 2, padding: "34px 30px 0" }} align="start">
          Settings
        </Typography>
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: "34px" }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="General Settings" />
            <Tab label="Version Upgrade" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <FormikProvider value={formik} style={{ padding: "0" }}>
            {loading ? (
              <CircularProgress />
            ) : (
              <Form>
              <Stack spacing={3}>
                <TextField
                  name="instaLink"
                  label="Instagram Link *"
                  fullWidth
                  {...getFieldProps("instaLink")}
                  error={Boolean(touched.instaLink && errors.instaLink)}
                  helperText={touched.instaLink && errors.instaLink}
                />

                <TextField
                  name="fbLink"
                  label="Facebook Link *"
                  {...getFieldProps("fbLink")}
                  error={Boolean(touched.fbLink && errors.fbLink)}
                  helperText={touched.fbLink && errors.fbLink}
                />

                <TextField
                  name="telegramLink"
                  label="Telegram Support *"
                  {...getFieldProps("telegramLink")}
                />

                <TextField
                  name="contactUsEmail"
                  label="Contact Us Email *"
                  {...getFieldProps("contactUsEmail")}
                  error={Boolean(
                    touched.contactUsEmail && errors.contactUsEmail
                  )}
                  helperText={touched.contactUsEmail && errors.contactUsEmail}
                />
                <TextField
                  name="companyAddress"
                  label="Company Address"
                  {...getFieldProps("companyAddress")}
                />

                <Stack spacing={2}>
                  <Typography variant="subtitle1">Contact Us Numbers</Typography>
                  {(formik.values.contactUs.length === 0 ? [""] : formik.values.contactUs).map((phone, index) => (
                    <Stack key={index} direction="row" spacing={2} alignItems="center">
                      <PhoneInput
                        country={"kh"}
                        value={phone}
                        onChange={(value) => {
                          const updatedContacts = [...formik.values.contactUs];
                          // When initial array was empty, updatedContacts may be empty, so handle carefully:
                          if (updatedContacts.length === 0) {
                            updatedContacts.push(value);
                          } else {
                            updatedContacts[index] = value;
                          }
                          formik.setFieldValue("contactUs", updatedContacts);
                        }}
                        inputProps={{
                          name: `contactUs[${index}]`,
                          required: true,
                        }}
                        inputStyle={{ width: "100%", height: "56px" }}
                      />
                      {formik.values.contactUs.length > 1 && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const updatedContacts = [...formik.values.contactUs];
                            updatedContacts.splice(index, 1);
                            formik.setFieldValue("contactUs", updatedContacts);
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                      )}
                      {index === formik.values.contactUs.length - 1 && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            const updatedContacts = [...formik.values.contactUs, ""];
                            formik.setFieldValue("contactUs", updatedContacts);
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Stack>
                  ))}

                </Stack>

                {/* <TextField
                  name="commission"
                  label="Dealer Commission (%) *"
                  {...getFieldProps("commission")}
                  error={Boolean(touched.commission && errors.commission)}
                  helperText={touched.commission && errors.commission}
                  inputProps={{
                    pattern: "^[0-9]*([.][0-9]{0,2})?$",
                    inputMode: "decimal",
                    maxLength: 8,
                  }}
                /> */}
                

                <TextField
                  name="deliveryFee"
                  label="Delivery Fee"
                  {...getFieldProps("deliveryFee")}
                  error={Boolean(touched.deliveryFee && errors.deliveryFee)}
                  helperText={touched.deliveryFee && errors.deliveryFee}
                  inputProps={{
                    pattern: "^[0-9]*([.][0-9]{0,2})?$",
                    inputMode: "decimal",
                    maxLength: 10,
                  }}
                />

                <TextField
                  name="rewardPoints"
                  label="Reward Points"
                  {...getFieldProps("rewardPoints")}
                  error={Boolean(touched.rewardPoints && errors.rewardPoints)}
                  helperText={touched.rewardPoints && errors.rewardPoints}
                  inputProps={{
                    pattern: "^[0-9]*([.][0-9]{0,2})?$",
                    inputMode: "decimal",
                    maxLength: 7,
                  }}

                />

                <TextField
                  name="rewardExpiry"
                  label="Reward Expiry"
                  {...getFieldProps("rewardExpiry")}
                  error={Boolean(touched.rewardExpiry && errors.rewardExpiry)}
                  helperText={touched.rewardExpiry && errors.rewardExpiry}
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: 3,
                  }}
                  onKeyDown={(e) => {
                    if (
                      !/[0-9]/.test(e.key) &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      e.key !== "ArrowLeft" &&
                      e.key !== "ArrowRight"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
                <TextField
                  name="rewardThresholdValue"
                  label="Reward Threshold Value"
                  {...getFieldProps("rewardThresholdValue")}
                  error={Boolean(
                    touched.rewardThresholdValue && errors.rewardThresholdValue
                  )}
                  helperText={
                    touched.rewardThresholdValue && errors.rewardThresholdValue
                  }
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: 3,
                  }}
                  onKeyDown={(e) => {
                    if (
                      !/[0-9]/.test(e.key) &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      e.key !== "ArrowLeft" &&
                      e.key !== "ArrowRight"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />

                {/* <TextField
                  name="dealerOrderRange"
                  label="Dealer Order Range (in meters) *"
                  {...getFieldProps("dealerOrderRange")}
                  error={Boolean(
                    touched.dealerOrderRange && errors.dealerOrderRange
                  )}
                  helperText={
                    touched.dealerOrderRange && errors.dealerOrderRange
                  }
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: 5,
                  }}
                  onKeyDown={(e) => {
                    if (
                      !/[0-9]/.test(e.key) &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      e.key !== "ArrowLeft" &&
                      e.key !== "ArrowRight"
                    ) {
                      e.preventDefault();
                    }
                  }}
                /> */}
              </Stack>

              <Button
                size="large"
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{ my: 2 }}
              >
                Update
              </Button>
              <Button
                size="large"
                variant="contained"
                onClick={() => {
                  if (!isSubmitting) navigate("/dashboard");
                }}
                sx={{ my: 2, ml: 4 }}
              >
                Cancel
              </Button>
            </Form>
          )}
          </FormikProvider>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {versionLoading ? (
            <CircularProgress />
          ) : (
            <Stack spacing={3}>
              <Typography variant="h6">App Versions</Typography>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Platform</strong></TableCell>
                      <TableCell><strong>App Type</strong></TableCell>
                      <TableCell><strong>Version</strong></TableCell>
                      <TableCell><strong>App Link</strong></TableCell>
                      <TableCell align="right"><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {versionData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No versions found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      versionData.map((version) => (
                        <TableRow
                          key={version._id}
                          sx={{
                            backgroundColor: editingVersion === version._id ? "action.selected" : "inherit",
                            "&:hover": { backgroundColor: "action.hover" },
                          }}
                        >
                          <TableCell>{version.platform?.toUpperCase() || "-"}</TableCell>
                          <TableCell>{version.appType || "-"}</TableCell>
                          <TableCell>{version.version || "-"}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: "300px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={version.applink}
                            >
                              {version.applink || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton
                                color="primary"
                                onClick={() => handleEditVersion(version)}
                                size="small"
                              >
                                <EditOutlined />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {editingVersion && (
                <FormikProvider value={versionFormik}>
                  <Form onSubmit={versionHandleSubmit}>
                    <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                      Edit Version
                    </Typography>
                    <Stack spacing={3}>
                      <FormControl fullWidth disabled>
                        <InputLabel>Platform</InputLabel>
                        <Select
                          label="Platform"
                          value={versionFormik.values.platform}
                          error={Boolean(versionTouched.platform && versionErrors.platform)}
                        >
                          <MenuItem value="android">Android</MenuItem>
                          <MenuItem value="ios">iOS</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth disabled>
                        <InputLabel>App Type</InputLabel>
                        <Select
                          label="App Type"
                          value={versionFormik.values.appType}
                          error={Boolean(versionTouched.appType && versionErrors.appType)}
                        >
                          <MenuItem value="driver">Driver</MenuItem>
                          <MenuItem value="nowgas">NowGas</MenuItem>
                        </Select>
                      </FormControl>

                    <TextField
                      name="version"
                      label="Version *"
                      fullWidth
                      {...versionGetFieldProps("version")}
                      error={Boolean(versionTouched.version && versionErrors.version)}
                      helperText={versionTouched.version && versionErrors.version}
                    />

                    <TextField
                      name="applink"
                      label="App Link *"
                      fullWidth
                      {...versionGetFieldProps("applink")}
                      error={Boolean(versionTouched.applink && versionErrors.applink)}
                      helperText={versionTouched.applink && versionErrors.applink}
                    />
                  </Stack>

                    <Button
                      size="large"
                      type="submit"
                      variant="contained"
                      disabled={versionIsSubmitting}
                      sx={{ my: 2 }}
                    >
                      {versionIsSubmitting ? "Saving..." : "Update"}
                    </Button>
                    <Button
                      size="large"
                      variant="outlined"
                      onClick={handleCancelEdit}
                      disabled={versionIsSubmitting}
                      sx={{ my: 2, ml: 2 }}
                    >
                      Cancel
                    </Button>
                  </Form>
                </FormikProvider>
              )}
            </Stack>
          )}
        </TabPanel>
      </CardContent>
    </Card>
  );
}

export default GlobalSetting;
